import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getAuthorDisplayName } from '../utils/displayName';
import useLiveRefresh from '../hooks/useLiveRefresh';
import './NetworkFeedSidebar.css';

export default function NetworkFeedSidebar({
  user,
  feedFilter = 'all',
  onFeedFilterChange,
}) {
  const API_URL = getApiBaseUrl();
  const userId = user?.id;
  const [tab, setTab] = useState(
    feedFilter === 'following' ? 'following' : 'connections'
  );
  const [connections, setConnections] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    if (!userId) {
      if (!silent) setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const [connRes, folRes] = await Promise.all([
        fetch(`${API_URL}/get_connections.php?user_id=${userId}`).then((r) =>
          r.json()
        ),
        fetch(`${API_URL}/get_following.php?user_id=${userId}`).then((r) =>
          r.json()
        ),
      ]);
      if (connRes.success) setConnections(connRes.connections || []);
      if (folRes.success) setFollowing(folRes.following || []);
    } catch (err) {
      console.error('Network sidebar load error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useLiveRefresh(() => load({ silent: true }), 20000, Boolean(userId));

  useEffect(() => {
    if (feedFilter === 'following') setTab('following');
    else if (feedFilter === 'connections') setTab('connections');
  }, [feedFilter]);

  const switchTab = (next) => {
    setTab(next);
    if (typeof onFeedFilterChange === 'function') {
      onFeedFilterChange(next);
    }
  };

  const showAllFeed = () => {
    if (typeof onFeedFilterChange === 'function') {
      onFeedFilterChange('all');
    }
  };

  const list = tab === 'following' ? following : connections;
  const preview = list.slice(0, 2);

  return (
    <aside className="nfs-panel">
      <div className="nfs-header">
        <h2 className="nfs-title">My Network</h2>
        <Link to="/network" className="nfs-manage">
          Manage
        </Link>
      </div>

      <div className="nfs-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'connections'}
          className={`nfs-tab ${tab === 'connections' ? 'active' : ''}`}
          onClick={() => switchTab('connections')}
        >
          Connections
          <span className="nfs-count">{connections.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'following'}
          className={`nfs-tab ${tab === 'following' ? 'active' : ''}`}
          onClick={() => switchTab('following')}
        >
          Following
          <span className="nfs-count">{following.length}</span>
        </button>
      </div>

      {feedFilter !== 'all' && (
        <button type="button" className="nfs-show-all" onClick={showAllFeed}>
          Show all discussions
        </button>
      )}

      <div className="nfs-list">
        {loading ? (
          <p className="nfs-empty">Loading…</p>
        ) : preview.length === 0 ? (
          <div className="nfs-empty-box">
            <p className="nfs-empty">
              {tab === 'following'
                ? 'You are not following anyone yet.'
                : 'No connections yet.'}
            </p>
            <Link to="/network?tab=suggestions" className="nfs-cta">
              Find people
            </Link>
          </div>
        ) : (
          preview.map((person) => {
            const name = getAuthorDisplayName(person);
            return (
              <Link
                key={person.id}
                to={`/profile/${person.id}`}
                className="nfs-person"
              >
                <img
                  src={resolveAvatarUrl(person, API_URL)}
                  alt=""
                  className="nfs-avatar"
                  onError={handleAvatarError}
                />
                <div className="nfs-person-info">
                  <span className="nfs-person-name">{name}</span>
                  {person.username && (
                    <span className="nfs-person-handle">@{person.username}</span>
                  )}
                  {person.position && (
                    <span className="nfs-person-meta">{person.position}</span>
                  )}
                  {(person.company || person.country) && (
                    <span className="nfs-person-meta">
                      {[person.company, person.country].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </div>
              </Link>
            );
          })
        )}
      </div>
    </aside>
  );
}
